// src/components/community/MediaSearch.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// MediaSearch component allows users to search for anime or manga and select one
const MediaSearch = ({ onSelect, mediaType, initialValue = null }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(initialValue);
  const searchTimeout = useRef(null);
  const dropdownRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search for anime or manga
  const searchMedia = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      // Use the confirmed working endpoint pattern
      const endpoint = `/api/${mediaType}/search?query=${encodeURIComponent(searchQuery)}`;
      console.log(`Searching ${mediaType} using endpoint: ${endpoint}`);
      
      const response = await axios.get(endpoint);
      
      // Process and set results
      const mediaResults = Array.isArray(response.data) ? response.data : (response.data.data || []);
      console.log(`Found ${mediaResults.length} results`);
      setResults(mediaResults);
    } catch (error) {
      console.error('Error searching media:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    // Set new timeout (300ms debounce)
    searchTimeout.current = setTimeout(() => {
      searchMedia(value);
    }, 300);

    // Show dropdown when typing
    if (value.trim()) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  // Handle media selection
  const handleSelect = (media) => {
    setSelectedMedia(media);
    setQuery(media.title || media.title_english || media.name);
    setShowDropdown(false);
    
    // Pass selected media to parent component
    onSelect({
      id: media._id,
      type: mediaType,
      title: media.title || media.title_english || media.name,
      image: media.image || (media.images?.jpg?.image_url)
    });
  };

  // Clear selected media
  const handleClear = () => {
    setSelectedMedia(null);
    setQuery('');
    onSelect(null);
  };

  return (
    <div className="media-search relative">
      <div className="flex items-center">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          placeholder={`Search for ${mediaType}...`}
          className="media-search-input"
        />
        {selectedMedia && (
          <button
            onClick={handleClear}
            className="media-clear-btn"
            aria-label="Clear selection"
          >
            ✕
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="media-search-dropdown"
        >
          {loading ? (
            <div className="media-search-loading">Loading...</div>
          ) : results.length > 0 ? (
            <ul className="media-search-results">
              {results.map((media) => (
                <li
                  key={media._id}
                  onClick={() => handleSelect(media)}
                  className="media-search-item"
                >
                  {media.images?.jpg?.image_url && (
                    <img
                      src={media.images.jpg.image_url}
                      alt={media.title}
                      className="media-item-image"
                    />
                  )}
                  <div className="media-item-info">
                    <div className="media-item-title">
                      {media.title || media.title_english || media.name}
                    </div>
                    <div className="media-item-details">
                      {mediaType === 'anime'
                        ? `${media.type || 'Unknown'} • Score: ${media.score || 'N/A'}`
                        : `${media.type || 'Manga'} • Score: ${media.score || 'N/A'}`}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : query.length > 0 ? (
            <div className="media-search-no-results">No results found</div>
          ) : null}
        </div>
      )}

      {/* Display selected media info */}
      {selectedMedia && (
        <div className="media-selected-container">
          {selectedMedia.images?.jpg?.image_url && (
            <img
              src={selectedMedia.images.jpg.image_url}
              alt={selectedMedia.title}
              className="media-selected-image"
            />
          )}
          <div className="media-selected-info">
            <div className="media-selected-title">
              {selectedMedia.title || selectedMedia.title_english || selectedMedia.name}
            </div>
            <div className="media-selected-details">
              {mediaType === 'anime'
                ? `${selectedMedia.type || 'Unknown'} • Score: ${selectedMedia.score || 'N/A'}`
                : `${selectedMedia.type || 'Manga'} • Score: ${selectedMedia.score || 'N/A'}`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaSearch;